import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DenominationData } from '@/app/types/dayEndClosure';
import { IndianRupee } from 'lucide-react';

interface DenominationCalculatorProps {
  initialData?: DenominationData;
  onTotalChange: (total: number, denominations: DenominationData) => void;
  expectedAmount?: number;
  title?: string;
  disabled?: boolean;
}

const DENOMINATIONS = ['2000', '500', '200', '100', '50', '20', '10', '5', '2', '1'];

export const DenominationCalculator: React.FC<DenominationCalculatorProps> = ({
  initialData = {},
  onTotalChange,
  expectedAmount,
  title = 'Cash Denomination Calculator',
  disabled = false
}) => {
  const [counts, setCounts] = useState<DenominationData>(() => {
    const initial: DenominationData = {};
    DENOMINATIONS.forEach(denom => {
      initial[denom] = initialData[denom] || 0;
    });
    return initial;
  });

  const calculateTotal = useCallback(() => {
    return DENOMINATIONS.reduce((total, denom) => {
      const denomValue = parseInt(denom);
      const count = counts[denom] || 0;
      return total + (denomValue * count);
    }, 0);
  }, [counts]);

  const calculateSubtotal = (denom: string) => {
    const denomValue = parseInt(denom);
    const count = counts[denom] || 0;
    return denomValue * count;
  };

  const updateCount = (denom: string, value: string) => {
    const numValue = parseInt(value) || 0;
    const newCounts = { ...counts, [denom]: Math.max(0, numValue) };
    setCounts(newCounts);
  };

  useEffect(() => {
    const total = calculateTotal();
    const nonZeroCounts = Object.entries(counts).reduce((acc, [denom, count]) => {
      if (count > 0) acc[denom] = count;
      return acc;
    }, {} as DenominationData);
    onTotalChange(total, nonZeroCounts);
  }, [counts, calculateTotal, onTotalChange]);

  const total = calculateTotal();
  const discrepancy = expectedAmount ? total - expectedAmount : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IndianRupee className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Denomination</TableHead>
              <TableHead className="w-[150px]">Count</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {DENOMINATIONS.map(denom => (
              <TableRow key={denom}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-1">
                    <IndianRupee className="w-3 h-3" />
                    {denom}
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min="0"
                    value={counts[denom] || ''}
                    onChange={(e) => updateCount(denom, e.target.value)}
                    placeholder="0"
                    className="w-full"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell className="text-right font-medium">
                  <div className="flex items-center justify-end gap-1">
                    <IndianRupee className="w-3 h-3" />
                    {calculateSubtotal(denom).toLocaleString('en-IN')}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-6 space-y-3 border-t pt-4">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>Total Physical Count:</span>
            <span className="flex items-center gap-1 text-primary">
              <IndianRupee className="w-4 h-4" />
              {total.toLocaleString('en-IN')}
            </span>
          </div>

          {expectedAmount !== undefined && (
            <>
              <div className="flex justify-between items-center">
                <span>Expected (Software) Balance:</span>
                <span className="flex items-center gap-1">
                  <IndianRupee className="w-3 h-3" />
                  {expectedAmount.toLocaleString('en-IN')}
                </span>
              </div>

              <div className={`flex justify-between items-center font-semibold ${
                discrepancy > 0 ? 'text-green-600' : discrepancy < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                <span>Discrepancy:</span>
                <span className="flex items-center gap-1">
                  {discrepancy !== 0 && (discrepancy > 0 ? '+' : '')}
                  <IndianRupee className="w-3 h-3" />
                  {Math.abs(discrepancy).toLocaleString('en-IN')}
                  {discrepancy > 0 && ' (Overage)'}
                  {discrepancy < 0 && ' (Shortage)'}
                  {discrepancy === 0 && ' (Balanced)'}
                </span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};